import { z } from 'zod';
/**
 * Primitives every resource shares. Defined once so a change to how ids or paging
 * work lands in one place rather than in every schema that happens to use them.
 *
 * `idSchema` is the unbranded base. The curriculum model brands it per resource in
 * `content.ts` — that deferral's own trigger, a second kind of id to confuse, fired
 * when it landed seven of them. Ids that belong to no resource stay plain here.
 */
export const idSchema = z
    .string()
    .min(1)
    .describe('Opaque resource identifier');
/** 017 §Shared pieces. */
export const replyIdSchema = idSchema.describe('Names this AI reply. Its traces and its rating use the same id.');
/** URL-safe, lowercase, hyphenated. Stable enough to appear in a path. */
export const slugSchema = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase, hyphenated, no spaces');
/** About twice the longest source name in the curriculum CSV, 245 — CURR-26. */
export const MAX_SOURCE_TITLE = 500;
export const timestampsSchema = z.object({
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Cursor paging rather than offset. Offset paging shifts under inserts, so a client
 * scrolling a list that is being written to sees duplicates and gaps; a cursor does
 * not. The cursor is opaque on purpose — clients must not decode it.
 */
export const paginationQuerySchema = z.object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/** Wraps any list response. `nextCursor` absent means the end of the collection. */
export function paginated(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().min(1).nullable(),
    });
}
/**
 * The offset is the client's, and it is untrusted.
 *
 * The learner's local date decides their streak (spec 012 §11), and storing an IANA
 * zone on `User` would both add an identifying attribute and go stale the moment they
 * travel. So the client says what its offset is and the server derives the date —
 * bounded to the real range, and cross-checked against the server's own UTC date so
 * nobody farms a streak by claiming to be four days ahead.
 */
export const utcOffsetMinutesSchema = z
    .number()
    .int()
    .min(-720)
    .max(840)
    .describe('Minutes east of UTC, as the client reports them. -720..840.');
/**
 * The same offset as a query, for the reads that print a streak.
 *
 * Coerced because a query string arrives as text, and defaulted because a caller
 * that sends none is answered in UTC rather than refused. Home, Explore and the
 * badge wall share it: the run they print is only alive or dead against a day,
 * and three encodings of that day is three streaks for one session.
 */
export const localDayQuerySchema = z
    .object({
    utcOffsetMinutes: z.coerce
        .number()
        .int()
        .min(-720)
        .max(840)
        .default(0)
        .describe('Minutes east of UTC, as the client reports them. -720..840.'),
})
    .describe('The learner’s own day, for a read that prints a streak.');
//# sourceMappingURL=common.js.map